/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLang } from 'hooks/lang';

import { loaders, issueStatuses } from 'entities';

import { Button, Card, Column, DatePicker, IconButton, Row, Table } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { ArrowRightIcon, ChatIcon, ListToDoIcon, RefreshIcon, UserIcon } from 'components/Icons';

import { useIssuesHome } from './context';

import { serializeDates } from 'utils/date';
import { truncateText } from 'utils/text';

const statusToConfig = {
  [issueStatuses.active.code]: {
    requireDate: false,
    title: 'active-issues',
  },

  [issueStatuses.closed.code]: {
    requireDate: true,
    title: 'closed-issues',
  },

  [issueStatuses.resolved.code]: {
    requireDate: true,
    title: 'resolved-issues',
  },
};

export function IssuesList() {
  const { t } = useLang();

  const { state, updateDates } = useIssuesHome();

  const { requireDate, title } = statusToConfig[state.status];

  const recruitments = loaders.projects.issuesByStatus.use({
    reloadOnChangeRef: [state.status, state.to.toISOString(), state.from.toISOString()].join(),

    params: {
      status: state.status,

      ...(requireDate
        ? serializeDates({
            startDate: state.from,
            endDate: state.to,
          })
        : {}),
    },

    defaultValue: [],

    refreshMinutes: 5,
  });

  return (
    <>
      <Column gap="6">
        <Row
          justifyContent="space-between"
          className="phone:flex-col phone:items-start phone:gap-0"
        >
          <h6 className="text-2xl text-primary">{t(title)}</h6>

          <Row gap="2">
            {requireDate && (
              <DatePicker
                mode="range"
                selected={state}
                onSelect={updateDates}
                className="border-0 text-sm"
              />
            )}

            <IconButton onClick={recruitments.retry}>
              <RefreshIcon size={14} />
            </IconButton>
          </Row>
        </Row>

        <Card>
          <Table
            items={recruitments.data.filter(({ status }) => status === state.status)}
            loading={recruitments.loading}
            uniqueKey="id"
            headerClassName="pb-3"
            columns={[
              {
                label: t('project')!,

                search: true,
                sort: true,

                getTextValue: ({ project }) => project.name,

                render: ({ project }) => (
                  <Button
                    size="fit"
                    variant="ghost"
                    to="projectProfile"
                    params={{ projectId: project.id }}
                    tooltipContent={project.name}
                    className="text-sm"
                  >
                    {truncateText(project.name, 35)}
                  </Button>
                ),
              },

              {
                label: t('type') as string,
                render: ({ type }) => t(type),
                filter: true,
              },

              {
                label: t('sub-type') as string,
                filter: true,
                getTextValue: ({ subType }) => t(subType),
                render: ({ subType, other }) => `${t(subType)}${other ? `(${other})` : ''}`,
              },

              {
                label: t('owner')!,
                filter: true,
                getTextValue: ({ owner }) => owner.name,
                render: ({ owner }) => <UserAvatar size={32} user={owner} name />,
              },

              {
                render: (issue) => {
                  return (
                    <Row justifyContent="flex-end">
                      <span className="flex w-[30px] items-center text-sm">
                        {issue.peopleCount} <UserIcon size={20} />
                      </span>

                      <span className="flex w-[30px] items-center text-sm">
                        {issue.actionCount} <ListToDoIcon size={20} />
                      </span>

                      <span className="flex w-[30px] items-center text-sm">
                        {issue.commentCount} <ChatIcon size={18} />
                      </span>

                      <IconButton
                        to="projectProfile"
                        params={{ projectId: issue.project.id, tab: 'issues', issueId: issue.id }}
                      >
                        <ArrowRightIcon size={18} />
                      </IconButton>
                    </Row>
                  );
                },
              },
            ]}
          />
        </Card>
      </Column>
    </>
  );
}
